import { useState, useEffect, useCallback } from 'react';
import { fetchReportById, listComponents, listElements, updateElement } from '../functionApi';
import { useAxiosInterceptor } from '../wrappers';
import type { ReportStato } from '../apiTypes';

/** Stati in cui l'editing dei driver e' consentito. Qualsiasi altro stato
 *  (drafted, approved, completed, ...) rende il report read-only. */
const EDITABLE_STATES: ReportStato[] = ['empty', 'generated', 'edited'];

export interface UseConductRiskDriverResult<T> {
  data: T | null;
  componentId: string | null;
  elementId: string | null;
  loading: boolean;
  error: string | null;
  /** Stato attuale del report (aggiornato al mount). */
  reportStatus: ReportStato | null;
  /** True quando lo stato del report NON e' fra EDITABLE_STATES
   *  ({'generated', 'edited'}). In tal caso l'editing dei driver e' bloccato. */
  isReadOnly: boolean;
  /** Salva il driver via POST .../edit. Rilancia in caso di errore (HTTP, network, validazione). */
  save: (value: T) => Promise<void>;
  /** Ricarica i dati del driver (utile dopo cambi parametri che il BE ricalcola). */
  refetch: () => Promise<void>;
}

export function useConductRiskDriver<T>(
  reportId: string | undefined,
  driverNumber: number,
): UseConductRiskDriverResult<T> {
  useAxiosInterceptor();
  const [data, setData] = useState<T | null>(null);
  const [componentId, setComponentId] = useState<string | null>(null);
  const [elementId, setElementId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportStatus, setReportStatus] = useState<ReportStato | null>(null);

  const load = useCallback(async () => {
    if (!reportId) return;
    const driverKind = `Driver${String(driverNumber).padStart(2, '0')}`;
    setLoading(true);
    setError(null);
    try {
      // Fetch dei componenti + status del report in parallelo. Lo status serve
      // per capire se il report e' approvato: in tal caso i singoli driver
      // devono avere il bottone Modifica disabilitato.
      const [components, report] = await Promise.all([
        listComponents(reportId),
        fetchReportById(reportId).catch(err => {
          console.warn('[CR driver] fetch report status fallito:', err);
          return null;
        }),
      ]);
      if (report) setReportStatus(report.status);

      const target = components.find((c) => c.kind === driverKind);

      if (!target) {
        setError(`Componente ${driverKind} non trovato nel report.`);
        setData(null);
        setComponentId(null);
        setElementId(null);
        return;
      }

      const elements = await listElements(reportId, target.id);
      const elementWithValue = elements.find((e) => e.value != null) ?? elements[0];

      if (!elementWithValue || elementWithValue.value == null) {
        setError(`Il driver ${driverKind} non è ancora stato generato.`);
        setData(null);
        setComponentId(target.id);
        setElementId(elementWithValue?.id ?? null);
        return;
      }

      setData(elementWithValue.value as T);
      setComponentId(target.id);
      setElementId(elementWithValue.id);
    } catch (err: any) {
      console.error('Errore caricamento driver Conduct Risk:', err);
      setError(err?.message ?? 'Errore nel caricamento del driver');
    } finally {
      setLoading(false);
    }
  }, [reportId, driverNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (value: T): Promise<void> => {
      if (!reportId || !componentId || !elementId) {
        throw new Error('Driver non identificato, impossibile salvare.');
      }
      const updated = await updateElement(reportId, componentId, elementId, { value });
      // Allinea lo stato remoto con la response del backend (potrebbe normalizzare il payload).
      if (updated?.value != null) setData(updated.value as T);
    },
    [reportId, componentId, elementId],
  );

  // Editing consentito SOLO se lo stato del report e' 'generated' o 'edited'.
  // Finche' reportStatus e' null (in caricamento) restiamo prudenzialmente
  // in read-only per non permettere modifiche premature.
  const isReadOnly = reportStatus == null || !EDITABLE_STATES.includes(reportStatus);

  return { data, componentId, elementId, loading, error, reportStatus, isReadOnly, save, refetch: load };
}
